import { FC, Ref, useCallback, useEffect, useImperativeHandle, useMemo } from 'react';
import * as vp from 'vopay';
import { useForm, withStyles, FieldData } from 'vopay';
import * as Yup from 'yup';

import { DetailsCard, DetailRow, Toggle } from 'UI';
import { useBillingFieldRenderer } from '../renderBillingField';
import { PLAN_MODEL_KEYS, isPermissionAllowedForMarket, railDisplayLabel } from '../planSettingHelpers';

import styles from './PlanSettingCard.module.scss';

const cn = withStyles(styles);

type ServiceFormValues = Record<string, string | boolean>;

export type PlanSettingCardHandle = {
  touchAllFields: () => void;
};

export type PlanSettingCardProps = {
  ref?: Ref<PlanSettingCardHandle | null>;
  serviceName: string;
  planSetting: vp.PlanSetting;
  currency: string;
  country: string;
  planType: 'pay as you go' | 'subscription';
  useProgressivePricing: boolean;
  onValidChange: (serviceName: string, isValid: boolean) => void;
  onValuesChange: (serviceName: string, values: Record<string, string | boolean>) => void;
};

const PlanSettingCard: FC<PlanSettingCardProps> = ({
  ref,
  serviceName,
  planSetting,
  currency,
  country,
  planType,
  useProgressivePricing,
  onValidChange,
  onValuesChange,
}) => {
  const renderBillingField = useBillingFieldRenderer(currency);

  const { permissions, maxAmounts, payAsYouGo, subscription, common, tiers } = planSetting;

  const servicePermEntries = useMemo(
    () =>
      Object.entries(permissions).filter(
        ([key]) => !PLAN_MODEL_KEYS.has(key) && isPermissionAllowedForMarket(key, currency, country),
      ),
    [permissions, currency, country],
  );

  const payAsYouGoEnabled = planType === 'pay as you go';
  const subscriptionEnabled = planType === 'subscription';

  const anyServicePermOn = useCallback(
    (values: ServiceFormValues) => servicePermEntries.some(([key]) => Boolean(values[key])),
    [servicePermEntries],
  );

  const fieldsVisible = useCallback(
    (values: ServiceFormValues) => servicePermEntries.length === 0 || anyServicePermOn(values),
    [servicePermEntries, anyServicePermOn],
  );

  const initialValues = useMemo<ServiceFormValues>(() => {
    const values: ServiceFormValues = {};

    for (const [k, fee] of Object.entries(permissions)) {
      if (PLAN_MODEL_KEYS.has(k)) continue;
      values[k] = Boolean(fee.value);
    }
    for (const [k, fee] of Object.entries(maxAmounts)) values[k] = fee.value ?? '';
    for (const [k, fee] of Object.entries(common)) values[k] = fee.value ?? '';
    for (const [k, fee] of Object.entries(payAsYouGo)) values[k] = fee.value ?? '';
    for (const [k, fee] of Object.entries(subscription)) values[k] = fee.value ?? '';
    for (const [k, tier] of Object.entries(tiers)) values[k] = tier.value ?? '';
    return values;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const makeFeeField = useCallback(
    (
      key: string,
      { type, label, options }: vp.PlanFee<string>,
      showField: (values: ServiceFormValues) => boolean,
    ): FieldData<ServiceFormValues> => ({
      fieldName: key,
      label: () => label,
      getInputState: () => (type !== 'boolean' ? 'required' : 'optional'),
      showField: showField,
      validate: () => (type === 'boolean' ? Yup.boolean() : Yup.string().required()),
      renderField: ({ getFormikData }) => renderBillingField(type, getFormikData, Object.values(options ?? {})),
      ...(['amount', 'percent', 'numeric'].includes(type) && {
        serializeField: (values: ServiceFormValues) => vp.sanitizeToNumeric(String(values[key] ?? '')) ?? '',
      }),
    }),
    [renderBillingField],
  );

  const fields = useMemo<FieldData<ServiceFormValues>[]>(() => {
    const result: FieldData<ServiceFormValues>[] = [];

    for (const [k, fee] of servicePermEntries) {
      result.push({
        fieldName: k,
        label: () => railDisplayLabel(fee.label || serviceName, currency),
        getInputState: () => 'optional',
        showField: () => true,
        validate: () => Yup.boolean(),

        renderField: ({ ctx, getFormikData }) => {
          const { name, value } = getFormikData<boolean>({});
          return (
            <Toggle
              name={name}
              checked={Boolean(value)}
              onChange={(e) => void ctx.setFieldValue(name, e.currentTarget.checked)}
            />
          );
        },
      });
    }

    for (const [k, fee] of Object.entries(maxAmounts)) {
      result.push(makeFeeField(k, fee, fieldsVisible));
    }

    for (const [k, fee] of Object.entries(common)) {
      result.push(makeFeeField(k, fee, fieldsVisible));
    }

    for (const [k, fee] of Object.entries(payAsYouGo)) {
      result.push(makeFeeField(k, fee, (values) => fieldsVisible(values) && payAsYouGoEnabled));
    }

    for (const [k, fee] of Object.entries(subscription)) {
      result.push(makeFeeField(k, fee, (values) => fieldsVisible(values) && subscriptionEnabled));
    }

    for (const [k, tier] of Object.entries(tiers)) {
      const options = Object.values(tier.options ?? {});
      result.push({
        fieldName: k,
        label: () => tier.label,
        getInputState: () => 'required',
        showField: (values) => fieldsVisible(values) && useProgressivePricing,
        validate: () => Yup.string().required(),
        renderField: ({ getFormikData }) => renderBillingField(tier.type, getFormikData, options),
      });
    }

    return result;
  }, [
    servicePermEntries,
    maxAmounts,
    common,
    payAsYouGo,
    subscription,
    tiers,
    fieldsVisible,
    makeFeeField,
    renderBillingField,
    payAsYouGoEnabled,
    subscriptionEnabled,
  ]);

  const permFieldNames = useMemo(() => new Set(servicePermEntries.map(([k]) => k)), [servicePermEntries]);
  const topLevelFieldNames = useMemo(
    () => new Set([...Object.keys(maxAmounts), ...Object.keys(tiers)]),
    [maxAmounts, tiers],
  );
  const nestedFieldNames = useMemo(
    () =>
      new Set([
        ...Object.keys(common),
        ...(payAsYouGoEnabled ? Object.keys(payAsYouGo) : []),
        ...(subscriptionEnabled ? Object.keys(subscription) : []),
      ]),
    [common, payAsYouGo, subscription, payAsYouGoEnabled, subscriptionEnabled],
  );

  const permFields = useMemo(() => fields.filter((f) => permFieldNames.has(f.fieldName)), [fields, permFieldNames]);
  const topLevelFields = useMemo(
    () => fields.filter((f) => topLevelFieldNames.has(f.fieldName)),
    [fields, topLevelFieldNames],
  );
  const nestedFields = useMemo(
    () => fields.filter((f) => nestedFieldNames.has(f.fieldName)),
    [fields, nestedFieldNames],
  );

  const hasNestedContent = nestedFieldNames.size > 0;

  const { ctx, renderFormElements } = useForm<ServiceFormValues>({
    config: fields,
    initialValues,
    onSubmit: () => {},
  });

  useImperativeHandle(
    ref,
    () => ({
      touchAllFields: () => {
        const touched: Record<string, boolean> = {};
        for (const field of fields) touched[field.fieldName] = true;
        void ctx.setTouched(touched);
      },
    }),
    [fields, ctx],
  );

  useEffect(() => {
    onValidChange(serviceName, ctx.isValid);
  }, [ctx.isValid]);

  useEffect(() => {
    const serialized = Object.fromEntries(
      fields.map((f) => [
        f.fieldName,
        f.serializeField ? (f.serializeField(ctx.values) ?? '') : ctx.values[f.fieldName],
      ]),
    ) as Record<string, string | boolean>;
    onValuesChange(serviceName, serialized);
  }, [ctx.values, fields, serviceName]);

  const permissionsOn = fieldsVisible(ctx.values);
  return (
    <DetailsCard>
      {servicePermEntries.length > 0 ? (
        <div
          className={[
            cn('permissionsRow'),
            permissionsOn && topLevelFields.length > 0 ? cn('permissionsRowBordered') : cn('permissionsRowLast'),
          ].join(' ')}
        >
          {renderFormElements({
            config: permFields,
            override: (fd) => (
              <div key={fd.fieldName} className={cn('permissionItem')}>
                <span className={cn('permissionLabel')}>{fd.label}</span>
                {fd.template}
              </div>
            ),
          })}
        </div>
      ) : (
        <DetailRow label={railDisplayLabel(serviceName, currency)} value={null} />
      )}
      <>
        {renderFormElements({
          config: topLevelFields,
          override: (fd) => (
            <DetailRow
              key={fd.fieldName}
              style={{ paddingTop: '12px', display: permissionsOn ? '' : 'none' }}
              label={
                <span className={cn('inputLabel')}>
                  {fd.label}
                  {fd.inputState === 'required' && <span className={cn('asterisk')}>*</span>}
                </span>
              }
              value={<div className={cn('inputWrapper')}>{fd.template}</div>}
            />
          ),
        })}
      </>

      <DetailsCard className={cn('nestedCard')} style={{ display: permissionsOn && hasNestedContent ? '' : 'none' }}>
        {renderFormElements({
          config: nestedFields,
          override: (fd) => (
            <DetailRow
              key={fd.fieldName}
              label={
                <span className={cn('inputLabel')}>
                  {fd.label}
                  {fd.inputState === 'required' && <span className={cn('asterisk')}>*</span>}
                </span>
              }
              value={<div className={cn('inputWrapper')}>{fd.template}</div>}
            />
          ),
        })}
      </DetailsCard>
    </DetailsCard>
  );
};

export default PlanSettingCard;
